import {useState, useContext} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {VideoContext} from '../../contexts/VideoContext'

import useMediaQuery from '@mui/material/useMediaQuery'

import Dialog from '@mui/material/Dialog'
import CloseIcon from '@mui/icons-material/Close'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import {Image, AspectImage} from '../generics'



const styles = {
  imageContainer: css`
    position: relative;
    width: 100%;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 0 200px 0 rgba(255, 0, 149, 0.2),
                0 0 150px 0 rgba(255, 0, 149, 0.2);

    @media (max-width: 1120px) {
      box-shadow:
        0 -150px 150px 0 rgba(255, 0, 149, 0.2),
        0 -150px 130px 0 rgba(255, 0, 149, 0.2);
    }

    @media (max-width: 960px) {
      max-width: 550px;
    }

    @media (max-width: 450px) {
      filter: none;
    }
  `,
  gradient: css`
    position: absolute;
    z-index: 10;
    top: 0; 
    left: 0; 
    right: 0; 
    bottom: 0;
    pointer-events: none;
    background-image: linear-gradient(
      to bottom,
      transparent, transparent, transparent,
      rgba(0,0,0,0.4), #000
    );
  `,
  mainButtonContainer: css`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 15;
    right: 20px;
  `,
  videoContainer: css`
    position: relative;
    width: 100%;
    transition: height 0.3s ease;
    
    @media (max-width: 960px) { 
      max-width: 550px; 
    }
  `,
  videoImageContainer: css`
    position: absolute; 
    top: 0; 
    left: 0; 
    width: 100%; 
    z-index: 4;

    @media (max-width: 1120px) { 
      position: relative; 
    }
  `,
  videoAspectOuterContainer: css`
    position: relative; 
    width: 100%; 
    z-index: 5;

    @media (max-width: 1120px) { 
      position: absolute; 
      top: 0; 
    }
  `,
  videoAspectInnerContainer: css`
    position: relative;`
  ,
  videoAspectRatioBox: css`
    position: relative; 
    width: 100%; 
    height: 0;
    overflow: hidden;
    transition: padding-top 0.3s ease;
  `,
  videoAspectRatioBoxInside: css`
    position: absolute; 
    top: 0; 
    left: 0; 
    width: 100%; 
    height: 100%;
  `,
  videoElement: css`
    width: 100%; 
    height: 100%; 
    object-fit: cover;
    transform: scale(0); 
    transition: transform 0.3s ease;
    cursor: pointer; 
    z-index: 5;
  `,
  videoCloseButton: css`
    position: absolute; 
    top: 10px; 
    right: 0; 
    z-index: 12;
    background: transparent;
    border: none; 
    color: #fff;
    transition: opacity 0.3s ease;
    z-index: 12; 
    cursor: pointer;
  `,
  videoControls: css`
    display: flex; 
    align-items: center; 
    position: absolute;
    bottom: 0; 
    left: 10px; 
    z-index: 12; 
    transition: transform 0.3s ease, opacity 0.3s ease;
    
    @media (max-width: 1120px) { 
      bottom: 30px; 
    }
  `,
  expandButton: css`
    border: none; 
    width: 45px; 
    height: 50px;
    cursor: pointer; 
    background: transparent; 
    z-index: 12;
  `,
  soundButton: css`
    border: none; 
    width: 45px; 
    height: 50px;
    cursor: pointer; 
    background: transparent; 
    z-index: 12;
  `,
}



function VideoControls({
  isMuted,
  expandRef,
  showExpand,
  controlsRef, 
  onToggleMute,
  onToggleExpand
}) {
  return (
    <div ref={controlsRef} css={styles.videoControls}>
      {showExpand && (
        <button ref={expandRef} onClick={onToggleExpand} css={styles.expandButton}>
          <ExpandMoreIcon fontSize='large' color='secondary' />
        </button>
      )}
      <button onClick={onToggleMute} css={styles.soundButton}>
        {isMuted ? (
          <VolumeOffIcon color='secondary' />
        ) : (
          <VolumeUpIcon color='secondary' />
        )}
      </button>
    </div>
  )
}



const DetailPageImageContainer = ({ type, video, poster, aspect, MainButton }) => {
  const {
    isMuted,
    videoRef,
    hideVideo,
    expandRef,
    aspectRef,
    toggleMute,
    controlsRef,
    toggleExpand,
    containerRef,
    isVideoActive
  } = useContext(VideoContext)

  const isLg = useMediaQuery('(min-width:1121px)')
  const isXs = useMediaQuery('(max-width:768px)')

  const [openDialog, setOpenDialog] = useState(false)

  // choose bottom offset in px:
  //   ≥1121px → 20
  //   ≤1120px & type==='performance' → 60
  //   ≤1120px & type!=='performance' → 40
  const bottomOffset = !isLg
    ? (type === 'performance' ? 60 : 30)
    : 20


  function handleOpenDialog(e) {
    if (isVideoActive) return
    e.stopPropagation()
    setOpenDialog(true)
  }

  function handleCloseDialog(e) {
    e.stopPropagation()
    setOpenDialog(false)
  }

  function handleCloseVideo(e) {
    e.stopPropagation()
    hideVideo()
  }


  return (
    <>
      <div id='image-container' css={styles.imageContainer} onClick={handleOpenDialog}>
        <div 
          css={styles.mainButtonContainer} 
          style={{bottom: `${bottomOffset}px`}}
          onClick={(e) => e.stopPropagation()}
        >
          {MainButton}
        </div>

        {!isLg && 
          <div css={styles.gradient} />
        }

        {!video ? (
          <AspectImage
            src={poster}
            aspect={aspect}
            borderRadius={isXs ? '0' : '8px'}
          />
        ) : (
          <>
            <div css={styles.videoContainer} ref={containerRef}>
              {/* large-screen controls */}
              {isVideoActive &&(
                <VideoControls
                  showExpand
                  expandRef={expandRef}
                  isMuted={isMuted}
                  onToggleExpand={toggleExpand}
                  onToggleMute={toggleMute}
                  controlsRef={controlsRef}
                />
              )}

              {/* poster behind video */}
              <div css={styles.videoImageContainer}>
                <AspectImage src={poster} aspect={aspect} borderRadius={isXs ? '0' : '8px'} />
              </div>

              <div css={styles.videoAspectOuterContainer}>
                <div css={styles.videoAspectInnerContainer}>
                  <div css={styles.videoAspectRatioBox} ref={aspectRef} style={{
                      paddingTop: aspect === '3:4' ? '133.33%' : '100%',
                    }}
                  >
                    {/* close button */}
                    {isVideoActive && (
                      <button onClick={handleCloseVideo} css={styles.videoCloseButton}>
                        <CloseIcon fontSize='large' />
                      </button>
                    )}

                    {/* actual <video> */}
                    <div css={styles.videoAspectRatioBoxInside}>
                      <video
                        ref={videoRef}
                        css={styles.videoElement}
                        muted={isMuted}
                        playsInline
                        controlsList='nodownload'
                        onClick={toggleMute}
                      >
                        <source src={video} type='video/mp4' />
                      </video>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>


      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='md'
        BackdropProps={{style: {backgroundColor: 'rgba(0,0,0,0.8)'}}}
        PaperProps={{style: {borderRadius: 0, maxHeight: '90%'}}}
      >
        <Image css={styles.dialogImage} src={poster} alt='full screen' />
      </Dialog>
    </>
  )
}


export default DetailPageImageContainer

